import styled from '@emotion/styled';
import { MOBILE_LOGO_SIZE, DESKTOP_LOGO_SIZE, DESKTOP_COLLAPSED_LOGO_SIZE } from 'screens/DeveloperPage/consts';
import { colorToAlpha } from 'helpers/insight';
import { H2, Text } from 'ds/components/typography';
import { typographyFontSizeCfg, typographyLineHeightCfg } from 'ds/components/typography/types';

export const COLLAPSED_CARD_HEIGHT = 64;
export const EXPANDED_CARD_HEIGHT = 171;

export const Wrapper = styled.div<{ collapsed: boolean; hasCollapsedBg: boolean; greyBg: boolean; }>`
  ${({ theme }) => `
    a:not(${UrbanRenewalLink}), a:not(${UrbanRenewalLink}):hover {
      color: ${theme.colors.neutrals.grey1};
    }
  `}
  ${({ theme, collapsed, hasCollapsedBg, greyBg }) => theme.media.showAt({ from: 2 })`
    transition: all .4s ease-in-out;
    padding: 0 ${theme.spacing(3)};
    height: ${EXPANDED_CARD_HEIGHT}px;
    display: flex;
    align-items: center;

    > div {
      width: 100%;
    }

    ${collapsed ? `
      ${hasCollapsedBg ? `
        background-color: ${colorToAlpha(theme.colors.neutrals.grey1, 0.85)};
        color: ${theme.colors.neutrals.white};
        a, a: hover {
          color: ${theme.colors.neutrals.white};
        }
      ` : `
        background-color: ${theme.colors.neutrals.grey11};
      `}
      position: relative;
      height: ${COLLAPSED_CARD_HEIGHT}px;
      > div {
        height: 100%;
        display: flex;
      }
    ` : `
      background-color: ${colorToAlpha(greyBg ? theme.colors.neutrals.grey11 : theme.colors.neutrals.white, 0.85)};
    `}
  `}

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    padding: ${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(2)};
  `}
`;

export const Inner = styled.div<{ collapsed: boolean; }>`
  display: flex;
  flex-grow: 1;
  align-items: center;
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
`;
export const ButtonsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  align-self: stretch;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const InfoWrapper = styled.div<{ collapsed: boolean; gapSize: number }>`
  ${({ theme, collapsed }) => theme.media.showAt({ from: 2 })`
    display: flex;
    ${collapsed ? '' : 'height: 123px;'}
  `}
  ${({ collapsed, theme, gapSize }) => `
    ${collapsed ? `
      margin-right: -${theme.spacing(4.5)};
    ` : `
      flex-direction: column;
      justify-content: center;
      gap: ${gapSize}px;
    `}
  `}
`;

export const PhoneLink = styled.a`
  text-decoration: none;
  display: flex;
`;

export const LogoWrapper = styled.div<{ hasLogo?: boolean; collapsed: boolean; }>`
  transition: all .4s ease;
  ${({ theme, hasLogo, collapsed }) => theme.media.showAt({ from: 2 })`
    padding: ${theme.spacing(0.5)};
    margin-left: ${theme.spacing(3)};
    background-color: ${hasLogo ? theme.colors.neutrals.white : theme.colors.neutrals.grey11};
    ${hasLogo ? '' : `border: 2px solid ${theme.colors.neutrals.white};`}
    border-radius: ${theme.borderRadius.small};
    box-shadow: 0px 0px 1px ${colorToAlpha(theme.colors.neutrals.grey1, 0.3)};
    height: ${DESKTOP_LOGO_SIZE}px;
    width: ${DESKTOP_LOGO_SIZE}px;
    ${collapsed ? `
      transform: scale(${DESKTOP_COLLAPSED_LOGO_SIZE / DESKTOP_LOGO_SIZE}) translateX(100px);
    ` : ''}
  `}

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    margin-left: ${theme.spacing(2)};
    box-shadow: 0px 0px 1px ${colorToAlpha(theme.colors.neutrals.grey1, 0.3)};
    border-radius: ${theme.borderRadius.small};
    overflow: hidden;
    flex-shrink: 0;
  `}
`;

export const LogoInner = styled.div<{ collapsed: boolean; }>`
  transition: all .4s ease;
  display: flex;
  align-items: center;
  justify-content: space-around;

  > img {
    width: 100%;
  }

  ${({ theme, collapsed }) => theme.media.showAt({ from: 2 })`
    height: 100%;
    width: 100%;
    ${collapsed ? `
      svg {
        transition: all .4s ease-in-out;
        width: 24px;
      }
      ` : ``}
  `}

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    height: ${MOBILE_LOGO_SIZE}px;
    width: ${MOBILE_LOGO_SIZE}px;
  `}
`;

export const LogoImg = styled.img`
  height: 100%;
  width: 100%;
  object-fit: contain;
`;

export const UrbanRenewalLink = styled(Text.withComponent('a'))`
  ${({ theme }) => `
    color: ${theme.colors.brand.main};
    font-weight: 500;
    line-height: 20px;
    cursor: pointer;
  `}
`;

export const ShareWrapper = styled.div`
  cursor: pointer;
  ${({ theme }) => theme.media.showAt({ at: 1 })`
    svg {
      background: none;
      height: 24px;
      width: 24px;
      padding: 0;

      > path {
        fill: ${theme.colors.neutrals.grey1};
      }
    }
  `}
`;

export const ExpandedShareWrapper = styled.div<{ hidden: boolean; }>`
  ${({ hidden }) => `
    display: ${hidden ? 'none' : 'flex'};
  `}
`;

export const InfoLine = styled(Text)<{ collapsed?: boolean; hasCollapsedBg?: boolean; }>`
  ${({ theme, collapsed, hasCollapsedBg }) => `
    display: flex;
    align-items: center;

    ${collapsed ? `
      flex-direction: row-reverse;

      svg {
        margin-right: ${theme.spacing(0.5)};
        > path {
          ${hasCollapsedBg ? `fill: ${theme.colors.neutrals.grey8};` : ''}
        }
      }
    ` : `
      svg {
        margin-left: ${theme.spacing(0.5)};
      }
    `}
  `}
`;

export const CollapsedShareWrapper = styled.div<{ collapsed?: boolean; hasCollapsedBg?: boolean; }>`
  ${({ theme, collapsed, hasCollapsedBg }) => collapsed ? `
    transform: translateX(33px);
    display: flex;
    align-items: center;
    margin-right: ${theme.spacing(1)};
    margin-top: -${theme.spacing(0.5)};
    flex-grow: 1;

    svg {
      height: 20px;
      width: 20px;
      background: none;
      padding: 0;

      ${hasCollapsedBg ? `
        > path {
          fill: ${theme.colors.neutrals.white};
        }
      ` : `
        > path {
          fill: ${theme.colors.neutrals.grey1};
        }
      `}
    }
  ` : `
    display: none;
  `}
`;

export const CollapsedBG = styled.div<{ src: string; }>`
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-size: cover;
  background-position: center;
}
  ${({ src }) => src ? `background-image: url("${src}");` : ''};
`;

export const NameLine = styled(H2.withComponent('h1'))<{ collapsed: boolean; }>`
  ${({ theme }) => theme.media.showAt({ from: 2 })`
    line-height: ${typographyLineHeightCfg.h2.mobile}px;
  `}
  ${({ collapsed }) => collapsed ? `
    &&& {
      transform: translateX(33px);
      font-size: ${typographyFontSizeCfg.h2.mobile}px;
      line-height: ${typographyLineHeightCfg.h2.mobile}px;
    }
  ` : ''}
`;

export const ContactWrapper = styled.div<{ collapsed: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row-reverse;
  ${({ theme, collapsed }) => theme.media.showAt({ from: 2 })`
    margin-right: auto;
    margin-bottom: ${collapsed ? '0' : 'auto'};
  `}
`;

export const ContactOrText = styled(Text)`
  ${({ theme }) => `
    margin: 0 ${theme.spacing(3)};
  `}
`;
